export interface SurveyPanelEntry {
  id: string;
  description: string;
  quantity: number;
  highlighted: boolean;
}

interface SurveyPanelSource {
  panels?: unknown;
  panelsDescription?: string | null;
  panelsQuantity?: string | number | null;
  highlightedFields?: unknown;
}

function parseQuantity(value: unknown, fallback = 0): number {
  if (value === null || value === undefined || value === '') return fallback;
  const quantity = typeof value === 'number' ? value : parseInt(String(value), 10);
  return Number.isFinite(quantity) && quantity >= 0 ? quantity : fallback;
}

function readRawPanels(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;

  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
}

export function normalizeSurveyPanels(source: SurveyPanelSource | null | undefined): SurveyPanelEntry[] {
  if (!source) return [];

  const rawPanels = readRawPanels(source.panels);
  const highlightedFields = source.highlightedFields && typeof source.highlightedFields === 'object'
    ? source.highlightedFields as Record<string, boolean>
    : {};
  const normalizedPanels = rawPanels
    .map((panel, index) => {
      const candidate = panel && typeof panel === 'object' ? (panel as Record<string, unknown>) : {};
      const description = String(candidate.description ?? '').trim();
      const quantity = parseQuantity(candidate.quantity, 1);

      if (!description) return null;

      return {
        id: String(candidate.id || `panel-${index + 1}`),
        description,
        quantity,
        highlighted: Boolean(candidate.highlighted),
      };
    })
    .filter((panel): panel is SurveyPanelEntry => panel !== null);

  if (normalizedPanels.length > 0) return normalizedPanels;

  if (source.panelsDescription || source.panelsQuantity) {
    return [
      {
        id: 'legacy',
        description: source.panelsDescription || '',
        quantity: parseQuantity(source.panelsQuantity),
        highlighted: Boolean(highlightedFields.panelsDescription),
      },
    ];
  }

  return [];
}

export function getPrimaryPanelFields(panels: SurveyPanelEntry[]): {
  panelsDescription: string;
  panelsQuantity: number | null;
} {
  const primaryPanel = panels[0];

  return {
    panelsDescription: primaryPanel?.description || '',
    panelsQuantity: primaryPanel ? primaryPanel.quantity : null,
  };
}

export function formatSurveyPanels(panels: SurveyPanelEntry[]): string {
  return panels
    .filter((panel) => panel.description || panel.quantity)
    .map((panel) => {
      const description = panel.description || 'Solar panel';
      return panel.quantity > 0 ? `${description} x ${panel.quantity}` : description;
    })
    .join(', ');
}
